// Bottom-of-thread composer for the Analyst.
//
// The sketch's floating "composer card": panel-2 surface, 22px radius,
// deep drop shadow. The textarea sits on top; the bottom row holds a
// paperclip (attach CSVs, ChatGPT-style) and the lime circular send
// button. Attached files render as removable chips above the textarea
// and are uploaded when the question is sent.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, IconButton, Stack, Typography, CircularProgress, alpha,
} from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { surfacesFor } from './uiTokens';


const MAX_FILES = 3;
const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB

const formatBytes = (n) => {
  const v = Number(n || 0);
  if (!v) return '';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
};

const isCsv = (file) => {
  const name = (file?.name || '').toLowerCase();
  const type = (file?.type || '').toLowerCase();
  return name.endsWith('.csv') || type.includes('csv');
};


const AskDataInput = ({
  value, onChange, onSubmit,
  isLoading, disabled,
  placeholder = 'Ask anything, or say what to build...',
  // Staged File objects, owned by the parent (cleared on send).
  files = [],
  onFilesChange = () => {},
}) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState(null);

  // Transient validation errors clear themselves.
  useEffect(() => {
    if (!fileError) return undefined;
    const id = setTimeout(() => setFileError(null), 4000);
    return () => clearTimeout(id);
  }, [fileError]);

  const canSend = !isLoading && !disabled
    && ((value || '').trim().length > 0 || files.length > 0);

  const handleKeyDown = (e) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    if (!picked.length) return;
    const next = [...files];
    for (const f of picked) {
      if (!isCsv(f)) {
        setFileError(`${f.name} isn't a CSV — only .csv files are supported.`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        setFileError(`${f.name} is too large (max ${formatBytes(MAX_FILE_BYTES)}).`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        setFileError(`You can attach up to ${MAX_FILES} files per question.`);
        break;
      }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    onFilesChange(next);
  };

  const removeFile = (idx) => {
    onFilesChange(files.filter((_, i) => i !== idx));
  };

  return (
    <Box
      sx={(theme) => {
        const s = surfacesFor(theme);
        return {
          width: '100%',
          borderRadius: '22px',
          border: `1px solid ${s.border2}`,
          bgcolor: s.panel2,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 18px 48px rgba(0,0,0,0.55)'
            : '0 12px 36px rgba(40,50,35,0.14)',
          p: '10px 12px 10px',
          transition: 'border-color 120ms',
          '&:focus-within': {
            borderColor: theme.palette.mode === 'dark'
              ? (theme.brand?.limeDark || theme.palette.primary.main)
              : theme.palette.primary.main,
          },
        };
      }}
    >
      {/* Staged attachments (not uploaded until send). */}
      {files.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, px: 0.5, pt: 0.25, pb: 1 }}>
          {files.map((f, i) => (
            <Stack
              key={`${f.name}-${i}`}
              direction="row" alignItems="center" spacing={0.75}
              sx={(theme) => {
                const s = surfacesFor(theme);
                return {
                  pl: 1.1, pr: 0.4, py: 0.45,
                  borderRadius: '10px',
                  border: `1px solid ${s.border2}`,
                  bgcolor: s.panel,
                  maxWidth: 260,
                };
              }}
            >
              <DescriptionOutlinedIcon
                sx={(theme) => ({
                  fontSize: 15,
                  color: theme.palette.mode === 'dark'
                    ? (theme.brand?.lime || theme.palette.primary.main)
                    : theme.palette.primary.main,
                })}
              />
              <Typography noWrap sx={(t) => ({ fontSize: 12, fontWeight: 600, color: surfacesFor(t).text })}>
                {f.name}
              </Typography>
              <Typography sx={(t) => ({ fontSize: 10.5, color: surfacesFor(t).faint, flexShrink: 0 })}>
                {formatBytes(f.size)}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove ${f.name}`}
                onClick={() => removeFile(i)}
                disabled={isLoading}
                sx={(t) => ({ p: 0.25, color: surfacesFor(t).faint, '&:hover': { color: surfacesFor(t).text } })}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Box
        component={TextareaAutosize}
        ref={textareaRef}
        aria-label="Ask a question"
        minRows={1}
        maxRows={8}
        placeholder={placeholder}
        value={value || ''}
        disabled={isLoading || disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={(theme) => {
          const s = surfacesFor(theme);
          return {
            display: 'block',
            width: '100%',
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: s.text,
            fontFamily: theme.typography.fontFamily,
            fontSize: 14,
            lineHeight: 1.55,
            padding: '6px 8px 14px 8px',
            boxSizing: 'border-box',
            '&::placeholder': { color: s.faint, opacity: 1 },
            '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
          };
        }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
        {/* Hidden picker driven by the paperclip. */}
        <Box
          component="input"
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          multiple
          onChange={handlePick}
          sx={{ display: 'none' }}
        />
        <IconButton
          size="small"
          aria-label="Attach a CSV file"
          disabled={isLoading || disabled}
          onClick={() => fileInputRef.current?.click()}
          sx={(theme) => {
            const s = surfacesFor(theme);
            return {
              width: 34, height: 34, borderRadius: '50%',
              color: s.muted,
              border: `1px solid ${s.border2}`,
              '&:hover': { color: s.text, borderColor: s.faint, bgcolor: 'transparent' },
              '&.Mui-disabled': { color: s.faint, opacity: 0.5 },
            };
          }}
        >
          <AttachFileIcon sx={{ fontSize: 17 }} />
        </IconButton>
        {fileError && (
          <Typography noWrap sx={(t) => ({ fontSize: 11.5, color: t.palette.error.main })}>
            {fileError}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={onSubmit}
          disabled={!canSend}
          size="small"
          aria-label="Send question"
          sx={(theme) => {
            const lime = theme.palette.mode === 'dark'
              ? (theme.brand?.lime || theme.palette.primary.main)
              : theme.palette.primary.main;
            const limeHover = theme.palette.mode === 'dark'
              ? (theme.brand?.limeDark || theme.palette.primary.dark)
              : theme.palette.primary.dark;
            const ink = theme.palette.mode === 'dark'
              ? (theme.brand?.ink || '#0a0c0a')
              : theme.palette.primary.contrastText;
            return {
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: canSend ? lime : alpha(lime, 0.25),
              color: canSend ? ink : alpha(ink, 0.6),
              boxShadow: canSend ? '0 4px 18px rgba(192,251,110,0.25)' : 'none',
              '&:hover': { bgcolor: canSend ? limeHover : alpha(lime, 0.3) },
              '&.Mui-disabled': {
                bgcolor: alpha(lime, theme.palette.mode === 'dark' ? 0.18 : 0.25),
                color: theme.palette.mode === 'dark' ? '#3d453d' : alpha(ink, 0.5),
              },
              transition: 'background-color 120ms, color 120ms, box-shadow 120ms',
            };
          }}
        >
          {isLoading
            ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
            : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>
    </Box>
  );
};

export default AskDataInput;
